import React, { useState, useEffect, useCallback } from 'react';
import { getThemeConfig, updateThemeConfig, getSavedThemes, saveTheme, applyTheme, deleteTheme } from './_request';
import { useAuth } from '../context/AuthContext';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';

const ThemeManagement = () => {
    const { authToken } = useAuth();
    const [theme, setTheme] = useState({
        colors: {
            primaryBG: '#F1FBFF',
            primary: '#009EDE',
            secondary: '#1BA2D4',
            grayFooter: '#797979',
            textBG: '#A7E6FF',
            heroText: '#000000',
        },
        fonts: {
            nastaleeqKasheeda: '"Jameel Noori Nastaleeq Kasheeda"',
            nastaleeqRegular: '"Jameel Noori Nastaleeq Regular"',
            alMajeed: '"Al Majeed Quranic Font"',
        }
    });
    const [savedThemes, setSavedThemes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [newThemeName, setNewThemeName] = useState('');

    const fetchThemeConfig = useCallback(async () => {
        try {
            const response = await getThemeConfig(authToken);
            if (response.error) {
                setMessage({ type: 'error', text: response.error });
            } else {
                setTheme(prev => ({
                    colors: { ...prev.colors, ...response.data.colors },
                    fonts: { ...prev.fonts, ...response.data.fonts }
                }));
            }
        } catch (error) {
            console.error('Error fetching theme configuration:', error);
            setMessage({ type: 'error', text: 'Failed to load theme configuration' });
        } finally {
            setLoading(false);
        }
    }, [authToken]);

    const fetchThemesList = useCallback(async () => {
        try {
            const response = await getSavedThemes();
            if (response && response.success) {
                setSavedThemes(response.data);
            }
        } catch (error) {
            console.error('Error fetching saved themes:', error);
        }
    }, []);

    useEffect(() => {
        const init = async () => {
            await fetchThemeConfig();
            await fetchThemesList();
        };
        init();
    }, [fetchThemeConfig, fetchThemesList]);

    const handleColorChange = (colorKey, value) => {
        if (/^#[0-9A-F]{6}$/i.test(value) || value === '') {
            setTheme(prev => ({
                ...prev,
                colors: {
                    ...prev.colors,
                    [colorKey]: value
                }
            }));
        }
    };

    const handleFontChange = (fontKey, value) => {
        setTheme(prev => ({
            ...prev,
            fonts: {
                ...prev.fonts,
                [fontKey]: value
            }
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage({ type: '', text: '' });
        try {
            const response = await updateThemeConfig(theme, authToken);
            if (response.error) {
                setMessage({ type: 'error', text: response.error });
            } else {
                setMessage({ type: 'success', text: 'Active theme updated successfully' });
            }
        } catch (error) {
            console.error('Error updating theme:', error);
            setMessage({ type: 'error', text: 'Failed to update theme' });
        } finally {
            setLoading(false);
        }
    };

    const handleApply = async (themeId) => {
        setLoading(true);
        setMessage({ type: '', text: '' });
        try {
            const response = await applyTheme(themeId);
            if (response.success) {
                setMessage({ type: 'success', text: response.message });
                setTheme(prev => ({
                    colors: { ...prev.colors, ...response.data.colors },
                    fonts: { ...prev.fonts, ...response.data.fonts }
                }));
            } else {
                setMessage({ type: 'error', text: response.message || 'Failed to apply theme' });
            }
        } catch (error) {
            console.error('Error applying theme:', error);
            setMessage({ type: 'error', text: 'Failed to apply theme' });
        } finally {
            setLoading(false);
        }
    };

    const handleSaveNewTheme = async (e) => {
        e.preventDefault();
        if (!newThemeName.trim()) {
            alert("Please enter a theme name");
            return;
        }
        setLoading(true);
        setMessage({ type: '', text: '' });
        try {
            const response = await saveTheme({
                name: newThemeName.trim(),
                colors: theme.colors,
                fonts: theme.fonts
            });
            if (response.success) {
                setMessage({ type: 'success', text: `Theme "${newThemeName}" saved successfully!` });
                setNewThemeName('');
                setShowSaveModal(false);
                await fetchThemesList();
            } else {
                setMessage({ type: 'error', text: response.message || 'Failed to save theme' });
            }
        } catch (error) {
            console.error('Error saving theme:', error);
            setMessage({ type: 'error', text: 'Failed to save theme' });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id, name) => {
        if (!window.confirm(`Are you sure you want to delete the theme "${name}"?`)) {
            return;
        }
        setLoading(true);
        setMessage({ type: '', text: '' });
        try {
            const response = await deleteTheme(id);
            if (response.success) {
                setMessage({ type: 'success', text: response.message || 'Theme deleted successfully' });
                await fetchThemesList();
            } else {
                setMessage({ type: 'error', text: response.message || 'Failed to delete theme' });
            }
        } catch (error) {
            console.error('Error deleting theme:', error);
            setMessage({ type: 'error', text: 'Failed to delete theme' });
        } finally {
            setLoading(false);
        }
    };

    if (loading && savedThemes.length === 0) {
        return <div style={{ textAlign: 'center', padding: '16px' }}><CircularProgress /></div>;
    }

    return (
        <div className="container mx-auto p-4 relative">
            <div className="flex justify-between items-center mb-6">
                <h1 className="font-bold text-3xl">Theme Management</h1>
                <div className="flex space-x-2">
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => setShowSaveModal(true)}
                        disabled={loading}
                    >
                        Save Current as Preset
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Save Active'}
                    </Button>
                </div>
            </div>

            {message.text && (
                <div className={`p-4 mb-6 rounded shadow-sm ${message.type === 'success' ? 'bg-green-50 border-l-4 border-green-500 text-green-700' : 'bg-red-50 border-l-4 border-red-500 text-red-700'}`}>
                    {message.text}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Active Theme Customizer Column */}
                <div className="lg:col-span-2 space-y-6">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="bg-white shadow border border-gray-100 rounded-xl p-6">
                            <h2 className="text-xl font-semibold mb-4 text-gray-800 border-b pb-2">Colors</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {Object.entries(theme.colors).map(([key, value]) => (
                                    <div key={key} className="space-y-2">
                                        <label className="block text-sm font-semibold text-gray-600 capitalize">
                                            {key.replace(/([A-Z])/g, ' $1').trim()}
                                        </label>
                                        <div className="flex items-center space-x-3">
                                            <div className="relative w-12 h-12 rounded-lg overflow-hidden border border-gray-300 shadow-inner flex-shrink-0">
                                                <input
                                                    type="color"
                                                    value={value}
                                                    onChange={(e) => handleColorChange(key, e.target.value)}
                                                    className="absolute -inset-2 w-[200%] h-[200%] cursor-pointer border-none"
                                                />
                                            </div>
                                            <input
                                                type="text"
                                                value={value}
                                                onChange={(e) => handleColorChange(key, e.target.value)}
                                                className="flex-1 px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-gray-700 text-sm"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="bg-white shadow border border-gray-100 rounded-xl p-6">
                            <h2 className="text-xl font-semibold mb-4 text-gray-800 border-b pb-2">Fonts</h2>
                            <div className="space-y-4">
                                {Object.entries(theme.fonts).map(([key, value]) => (
                                    <div key={key} className="space-y-2">
                                        <label className="block text-sm font-semibold text-gray-600 capitalize">
                                            {key.replace(/([A-Z])/g, ' $1').trim()}
                                        </label>
                                        <input
                                            type="text"
                                            value={value}
                                            onChange={(e) => handleFontChange(key, e.target.value)}
                                            className="w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-gray-700 text-sm"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </form>
                </div>

                {/* Presets List Column */}
                <div className="space-y-6">
                    <div className="bg-white shadow border border-gray-100 rounded-xl p-6">
                        <h2 className="text-xl font-semibold mb-4 text-gray-800 border-b pb-2">Theme Presets</h2>
                        <div className="space-y-4 max-h-[600px] overflow-y-auto pr-1">
                            {savedThemes.map((item) => (
                                <div key={item.id} className="border border-gray-100 rounded-lg p-4 bg-gray-50/50 hover:bg-gray-50 transition shadow-sm flex flex-col justify-between">
                                    <div>
                                        <h3 className="font-semibold text-gray-800 text-sm">{item.name}</h3>
                                        {/* Color badges */}
                                        <div className="flex space-x-1.5 mt-2">
                                            {Object.entries(item.colors).map(([colorKey, colorVal]) => (
                                                <div
                                                    key={colorKey}
                                                    style={{ backgroundColor: colorVal }}
                                                    className="w-6 h-6 rounded-full border border-white shadow-sm"
                                                    title={`${colorKey}: ${colorVal}`}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                    <div className="flex justify-end space-x-2 mt-4 pt-3 border-t border-gray-100">
                                        <Button
                                            variant="text"
                                            size="small"
                                            color="primary"
                                            onClick={() => handleApply(item.id)}
                                            disabled={loading}
                                        >
                                            Apply
                                        </Button>
                                        {/* Hide delete button for system defaults 1-4 */}
                                        {item.id > 4 && (
                                            <Button
                                                variant="text"
                                                size="small"
                                                color="error"
                                                onClick={() => handleDelete(item.id, item.name)}
                                                disabled={loading}
                                            >
                                                Delete
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {savedThemes.length === 0 && (
                                <p className="text-gray-500 text-sm text-center py-4">No presets found. Create one using "Save Current as Preset".</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Save Preset Dialog Modal */}
            {showSaveModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-xl shadow-xl border max-w-md w-full p-6 space-y-4 animate-in fade-in zoom-in-95 duration-150">
                        <h3 className="text-xl font-bold text-gray-800">Save Theme Preset</h3>
                        <p className="text-sm text-gray-500">Provide a name to save the current colors and fonts configuration as a selectable preset.</p>
                        <form onSubmit={handleSaveNewTheme} className="space-y-4">
                            <input
                                type="text"
                                placeholder="Theme Name (e.g. Autumn Gold)"
                                value={newThemeName}
                                onChange={(e) => setNewThemeName(e.target.value)}
                                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium text-gray-700"
                                required
                                autoFocus
                            />
                            <div className="flex justify-end space-x-2">
                                <Button
                                    variant="outlined"
                                    color="inherit"
                                    onClick={() => {
                                        setShowSaveModal(false);
                                        setNewThemeName('');
                                    }}
                                    disabled={loading}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={loading}
                                >
                                    Save Preset
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ThemeManagement;